import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Platform } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { AuthService as ProductsAuthService, activeCatalogSelector } from '@vi-succ/products-domain';
import { AuthService as ShoppingCartAuthService } from '@vi-succ/shoppingcart-domain';
import { jwtDecode } from 'jwt-decode';
import { NGXLogger } from 'ngx-logger';
import {
  BehaviorSubject,
  Observable,
  Subject,
  combineLatest,
  debounceTime,
  distinct,
  filter,
  first,
  map,
  of,
  share,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import { BrowserService } from '../../../services/browser.service';
import { StorageService } from '../../../services/storage.service';
import { ContextInfo } from '../models/login.model';
import { loadUserRequest, logout, selectLoading, selectUserContextInfo } from '../state';

@Injectable({ providedIn: 'root' })
export class AuthService implements ProductsAuthService, ShoppingCartAuthService, OnDestroy {
  private static readonly CSRF_TOKEN_STORAGE_KEY = 'csrf_token';
  fetchNewCsrfToken$: Observable<{ token: string }> = of({ token: '' });
  isEmployee$ = this.store.select(selectUserContextInfo).pipe(map((contextInfo) => !contextInfo?.customer));
  contextInfoLoading$ = this.store.select(selectLoading);
  userInfo$ = this.store.select(selectUserContextInfo).pipe(map((contextInfo) => contextInfo));
  private destroy$ = new Subject<void>();
  private csrfToken$ = new BehaviorSubject<string | undefined>(undefined);
  authenticated$: Observable<boolean> = this.store.select(selectUserContextInfo).pipe(
    map((contextInfo) => {
      return !!contextInfo;
    }),
    startWith(false),
  );
  enableCheckout$ = combineLatest([this.store.select(selectUserContextInfo), this.store.select(selectLoading)]).pipe(
    map(([contextInfo, loading]) => {
      if (contextInfo) {
        return !contextInfo.roles.includes('liteaccount');
      } else if (loading) {
        return false;
      }
      return true;
    }),
    // Add debouceTime to prevent flickering
    debounceTime(100),
    startWith(false),
  );

  showNetPrice$ = combineLatest([this.store.select(selectUserContextInfo), this.store.select(selectLoading)]).pipe(
    map(([contextInfo, loading]) => {
      if (environment.checkNetPriceRole) {
        if (contextInfo) {
          return contextInfo.roles.includes('SHOW_NETTO');
        } else if (loading) {
          return false;
        }
        return true;
      } else {
        return true;
      }
    }),
    startWith(false),
  );

  canOrder$ = combineLatest([this.store.select(selectUserContextInfo), this.store.select(activeCatalogSelector)]).pipe(
    map(([contextInfo, catalog]) => {
      if (contextInfo && contextInfo.user) {
        return !contextInfo.user.noOrder;
      } else {
        return !catalog || !environment.noOrderSalesOrgs.includes(catalog.salesOrg);
      }
    }),
    startWith(true),
  );

  constructor(
    private http: HttpClient,
    private store: Store,
    private platform: Platform,
    private logger: NGXLogger,
    private zone: NgZone,
    private storageService: StorageService,
    private browserService: BrowserService,
  ) {}

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async init() {
    this.csrfToken$.pipe(distinct(), takeUntil(this.destroy$)).subscribe((jwtToken) => {
      if (jwtToken) {
        this.storageService.set(AuthService.CSRF_TOKEN_STORAGE_KEY, jwtToken).then(() => {
          this.logger.debug('set csrf token in storage');
          this.store.dispatch(loadUserRequest());
        });
      }
    });

    this.fetchNewCsrfToken$ = this.getCsrfTokenRequest().pipe(
      takeUntil(this.destroy$),
      tap({
        next: ({ token }) => this.csrfToken$.next(token),
        error: () => this.redirectToLoginScreen(),
      }),
      share(),
    );

    // Run innerInitialize on app start for reading token from storage
    await this.innerInitialize(window.location.hash);
    // Run init on app start for reading token from url
    if (this.platform.is('capacitor')) {
      this.extractTokenNative();
    } else {
      this.extractTokenWeb();
    }
  }

  login(callback?: string) {
    this.redirectToLoginScreen(callback);
  }

  register() {
    const url = `${
      environment.backend.accountUrl
    }/register?hideNoCompanyNumberOption=true&redirectUrl=${encodeURIComponent(environment.selfUrl)}`;

    this.browserService.open(url, '_self', 'Viessmann Account');
  }

  logout() {
    this.removeTokenFromStorage();
    this.csrfToken$.next(undefined);
    this.samlLogout();
  }

  updateToken(newToken: string) {
    this.csrfToken$.next(newToken);
  }

  isCSRFTokenExpired(token: string): boolean {
    const date = this.getTokenExpirationDate(token);
    if (!date) {
      return false;
    }
    return date.valueOf() <= new Date().valueOf();
  }

  getTokenExpirationDate(token: string): Date | null {
    const decoded = <{ exp?: number }>jwtDecode(token);

    if (decoded.exp === undefined) {
      return null;
    }

    const date = new Date(0);
    date.setUTCSeconds(decoded.exp);
    return date;
  }

  getTokenIdentityType(): string | null {
    const token = this.getCsrfToken();
    if (!token) return null;

    const { identityType } = jwtDecode<{ identityType: string }>(token);
    return identityType ?? null;
  }

  getTokenUuid(): string | null {
    const token = this.getCsrfToken();
    if (!token) return null;

    const { 'x-uuid': uuid } = jwtDecode<{ 'x-uuid': string }>(token);
    return uuid ?? null;
  }

  getUserIdentity(): Observable<{ identityType: string; uuid: string } | null> {
    return this.csrfToken$.pipe(
      startWith(undefined),
      map((token) => {
        if (!token) return null;

        const { 'x-uuid': uuid } = jwtDecode<{ 'x-uuid': string }>(token);
        const { identityType } = jwtDecode<{ identityType: string }>(token);

        return !!identityType && !!uuid ? { identityType, uuid } : null;
      }),
    );
  }

  hasValidCsrfToken(): boolean {
    const token = this.getCsrfToken();
    return token !== undefined && this.isCsrfTokenValid(token);
  }

  getCsrfToken(): string | undefined {
    return this.csrfToken$.value;
  }

  fetchUserData() {
    return this.csrfToken$.pipe(
      filter((token) => !!token),
      first(),
      switchMap(() => {
        return this.http.get<ContextInfo>(`${environment.backend.url}/contextinfo`, {
          headers: {
            Authorization: `CSRF ${this.getCsrfToken()}`,
          },
        });
      }),
    );
  }

  getCsrfTokenRequest() {
    return this.http.post<{ token: string }>(
      `${environment.iam.auth.baseUrl}/auth/v1/saml/csrf`,
      {
        appId: environment.iam.auth.appId,
      },
      {
        withCredentials: true,
      },
    );
  }

  private extractTokenNative() {
    App.addListener('appUrlOpen', ({ url }) => {
      const u = new URL(url);
      this.zone.run(() => {
        const slug = url.split('capacitor/com.viessmann.viparts').pop();
        if (slug !== '/cart/complete') {
          this.innerInitialize(u.hash);
        }
      });
    });
  }

  private extractTokenWeb() {
    this.innerInitialize(window.location.hash);
  }

  private async innerInitialize(hash: string) {
    const token = this.getCsrfTokenFromUrl(hash) || (await this.getCsrfTokenFromStorage());

    if (token && this.isCsrfTokenValid(token)) {
      await this.storageService.set(AuthService.CSRF_TOKEN_STORAGE_KEY, token);
      this.csrfToken$.next(token);
    } else if (token && !this.isCsrfTokenValid(token)) {
      await this.removeTokenFromStorage();
      this.fetchNewCsrfToken$.subscribe();
    }
    try {
      await Browser.close();
    } catch (err: unknown) {
      if (err instanceof Error) {
        this.logger.error(err.message);
      }
    }
  }

  private async removeTokenFromStorage() {
    await this.storageService.remove(AuthService.CSRF_TOKEN_STORAGE_KEY);
  }

  private samlLogout() {
    this.store.dispatch(logout());
    if (this.platform.is('capacitor')) {
      this.logoutApp();
    } else {
      this.logoutWeb();
    }
  }

  private logoutApp() {
    // Automatic closing is only supported on web and ios
    Browser.addListener('browserPageLoaded', () => {
      Browser.close().then(() => {
        Browser.removeAllListeners();
      });
    });

    Browser.addListener('browserPageLoaded', () => {
      Browser.removeAllListeners().then(() => {
        Browser.close();
      });
    });
    const url = `${environment.iam.url}/idp/v3/sso/logout?redirectUrl=${encodeURIComponent(this.getRedirectUri())}`;
    this.browserService.open(url, '_self', 'Viessmann IAM');
  }

  private logoutWeb() {
    localStorage.removeItem('csrf_token');
    const form = window.document.createElement('form');
    form.setAttribute('method', 'post');
    form.setAttribute(
      'action',
      `${environment.iam.auth.baseUrl}/saml/logout?redirectUrl=${encodeURIComponent(this.getRedirectUri())}`,
    );
    form.setAttribute('target', '_self');

    window.document.body.appendChild(form);
    form.submit();
  }

  private fetchNewCsrfToken() {
    return this.fetchNewCsrfToken$;
  }

  private getCsrfTokenFromStorage(): Promise<string> {
    return this.storageService.get<string>(AuthService.CSRF_TOKEN_STORAGE_KEY);
  }

  private getCsrfTokenFromUrl(hash: string): string | void {
    if (hash?.startsWith('#token=')) {
      const value = new HttpParams({ fromString: hash }).get('#token');
      window.location.hash = ''; // remove token form url
      return value || undefined;
    }
    return undefined;
  }

  private redirectToLoginScreen(callback?: string) {
    const redirectUri = this.getRedirectUri(callback);
    const redirectTo = `${environment.iam.auth.baseUrl}/saml/sso/request?appId=${
      environment.iam.auth.appId
    }&redirectUrl=${encodeURIComponent(redirectUri)}`;
    this.browserService.open(redirectTo, '_self', 'Viessmann IAM');
  }

  private getRedirectUri(callback?: string) {
    let redirectUrl = this.platform.is('capacitor')
      ? 'com.viessmann.viparts://viparts.viessmann.com/capacitor/com.viessmann.viparts/callback'
      : window.location.origin;
    if (callback) redirectUrl = `${redirectUrl}/${callback}`;
    return redirectUrl;
  }

  private isCsrfTokenValid(token: string) {
    try {
      return !this.isCSRFTokenExpired(token);
    } catch (_) {
      return false;
    }
  }
}
